
import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Plus, Edit, Trash2, ExternalLink } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useModulesForDropdown } from '@/hooks/useModulesForDropdown'
import { useSchools } from '@/hooks/useSchools'

const AdminResources = () => {
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingResource, setEditingResource] = useState(null)
  const [activeTab, setActiveTab] = useState('global')
  const { toast } = useToast()
  const { modules: allModules, loading: modulesLoading } = useModulesForDropdown()
  const { schools, loading: schoolsLoading } = useSchools()

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    module: '',
    domain: '',
    type: 'discover',
    file_url: '',
    school_id: ''
  })

  const domains = [
    { value: 'self', label: 'Self' },
    { value: 'wealth', label: 'Wealth' },
    { value: 'social', label: 'Social' }
  ]

  // Filter modules by selected domain using actual module categories
  const availableModules = allModules.filter(module => {
    if (!formData.domain) return true // Show all if no domain selected
    
    // Map domain selection to actual module categories
    if (formData.domain === 'wealth') {
      return module.category === 'Wealth'
    } else if (formData.domain === 'self') {
      return module.category === 'Self'
    } else if (formData.domain === 'social') {
      return module.category === 'Social'
    }
    return false
  })

  // Debug logging
  console.log('AdminResources - Module filtering:', {
    selectedDomain: formData.domain,
    allModulesCount: allModules.length,
    availableModulesCount: availableModules.length,
    availableModules: availableModules.map(m => ({ label: m.label, category: m.category }))
  })

  useEffect(() => {
    fetchResources()

    // Set up real-time subscription for resources
    const resourcesChannel = supabase
      .channel('admin-resources-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'admin_resources'
        },
        () => {
          fetchResources()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(resourcesChannel)
    }
  }, [])

  const fetchResources = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_resources')
        .select(`
          *,
          schools (
            id,
            name
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error
      setResources(data || [])
    } catch (error: any) {
      toast({
        title: "Error fetching resources",
        description: error.message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  // Filter resources based on active tab
  const filteredResources = resources.filter((resource: any) => {
    if (activeTab === 'global') {
      return !resource.school_id
    } else {
      return resource.school_id
    }
  })

  // Group school-specific resources by school
  const resourcesBySchool = filteredResources.reduce((acc: any, resource: any) => {
    if (resource.school_id) {
      const schoolName = resource.schools?.name || 'Unknown School'
      if (!acc[schoolName]) {
        acc[schoolName] = []
      }
      acc[schoolName].push(resource)
    }
    return acc
  }, {})

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      if (editingResource) {
        const { error } = await supabase
          .from('admin_resources')
          .update({
            ...formData,
            school_id: formData.school_id === 'global' ? null : formData.school_id || null
          })
          .eq('id', editingResource.id)
        
        if (error) throw error
        toast({ title: "Resource updated successfully" })
      } else {
        const { error } = await supabase
          .from('admin_resources')
          .insert({
            ...formData,
            school_id: formData.school_id === 'global' ? null : formData.school_id || null
          })
        
        if (error) throw error
        toast({ title: "Resource created successfully" })
      }

      setDialogOpen(false)
      setEditingResource(null)
      setFormData({
        title: '',
        description: '',
        module: '',
        domain: '',
        type: 'discover',
        file_url: '',
        school_id: ''
      })
      fetchResources()
    } catch (error: any) {
      toast({
        title: "Error saving resource",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleEdit = (resource: any) => {
    setEditingResource(resource)
    setFormData({
      title: resource.title,
      description: resource.description || '',
      module: resource.module || '',
      domain: resource.domain || '',
      type: resource.type || 'discover',
      file_url: resource.file_url || '',
      school_id: resource.school_id || 'global'
    })
    setDialogOpen(true)
  }

  const handleDelete = async (resourceId: string) => {
    try {
      const { error } = await supabase
        .from('admin_resources')
        .delete()
        .eq('id', resourceId)

      if (error) throw error
      toast({ title: "Resource deleted successfully" })
      fetchResources()
    } catch (error: any) {
      toast({
        title: "Error deleting resource",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Resource Management</CardTitle>
          <CardDescription>Loading resources...</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Resource Management</CardTitle>
            <CardDescription>Create and manage admin resources. Set type as 'discover' for general resources or 'inspiration' for quiz mappings.</CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setEditingResource(null)
                setFormData({
                  title: '',
                  description: '',
                  module: '',
                  domain: '',
                  type: 'discover',
                  file_url: '',
                  school_id: ''
                })
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Resource
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingResource ? 'Edit Resource' : 'Create New Resource'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="discover">Discover</SelectItem>
                      <SelectItem value="inspiration">Inspiration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Select value={formData.domain} onValueChange={(value) => setFormData({ ...formData, domain: value, module: '' })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select domain" />
                    </SelectTrigger>
                    <SelectContent>
                      {domains.map((domain) => (
                        <SelectItem key={domain.value} value={domain.value}>
                          {domain.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="module">Module</Label>
                  <Select value={formData.module} onValueChange={(value) => setFormData({ ...formData, module: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder={modulesLoading ? "Loading..." : "Select module"} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableModules.map((module) => (
                        <SelectItem key={module.value} value={module.value}>
                          {module.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="school_id">Target University</Label>
                  <Select value={formData.school_id} onValueChange={(value) => setFormData({ ...formData, school_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select target university (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">Global (All Universities)</SelectItem>
                      {schools.map(school => (
                        <SelectItem key={school.id} value={school.id}>{school.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Input
                    id="file_url"
                    value={formData.file_url}
                    onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                    placeholder="https://example.com/file.pdf"
                  />
                </div>
                <Button type="submit" className="w-full">
                  {editingResource ? 'Update Resource' : 'Create Resource'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="global">Global Resources</TabsTrigger>
            <TabsTrigger value="school-specific">School-Specific Resources</TabsTrigger>
          </TabsList>
          
          <TabsContent value="global" className="mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Module</TableHead>
                  <TableHead>Domain</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredResources.map((resource: any) => (
                  <TableRow key={resource.id}>
                    <TableCell className="font-medium">{resource.title}</TableCell>
                    <TableCell>
                      <Badge variant={resource.type === 'discover' ? 'default' : 'secondary'}>
                        {resource.type}
                      </Badge>
                    </TableCell>
                    <TableCell>{resource.module}</TableCell>
                    <TableCell>{resource.domain}</TableCell>
                    <TableCell>
                      {resource.file_url ? (
                        <a 
                          href={resource.file_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-blue-600 hover:text-blue-800"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(resource)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(resource.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredResources.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No global resources created yet. Click "Add Resource" to create your first resource.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="school-specific" className="mt-4">
            {Object.keys(resourcesBySchool).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No school-specific resources created yet. Click "Add Resource" and select a university to create school-specific resources.
              </div>
            ) : (
              Object.entries(resourcesBySchool).map(([schoolName, schoolResources]: [string, any]) => (
                <div key={schoolName} className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">{schoolName}</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Module</TableHead>
                        <TableHead>Domain</TableHead>
                        <TableHead>File</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {schoolResources.map((resource: any) => (
                        <TableRow key={resource.id}>
                          <TableCell className="font-medium">{resource.title}</TableCell>
                          <TableCell>
                            <Badge variant={resource.type === 'discover' ? 'default' : 'secondary'}>
                              {resource.type}
                            </Badge>
                          </TableCell>
                          <TableCell>{resource.module}</TableCell>
                          <TableCell>{resource.domain}</TableCell>
                          <TableCell>
                            {resource.file_url ? (
                              <a 
                                href={resource.file_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-blue-600 hover:text-blue-800"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(resource)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(resource.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}

export default AdminResources
